/**
 * Customer form validation utilities
 * Provides comprehensive validation for customer data before submission
 */

/**
 * Validates customer name
 * @param {string} customerName - The customer name to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateCustomerName = (customerName) => {
  if (!customerName || !customerName.trim()) {
    return 'Customer Name is required';
  }
  
  if (customerName.trim().length < 2) {
    return 'Customer Name must be at least 2 characters';
  }
  
  if (customerName.length > 200) {
    return 'Customer Name must not exceed 200 characters';
  }
  
  return null;
};

/**
 * Validates customer code
 * @param {string} customerCode - The customer code to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateCustomerCode = (customerCode) => {
  // Customer code is optional, but if provided must meet requirements
  if (!customerCode || !customerCode.trim()) {
    return null; // Optional field
  }
  
  if (customerCode.trim().length < 2) {
    return 'Customer Code must be at least 2 characters';
  }
  
  if (customerCode.length > 50) {
    return 'Customer Code must not exceed 50 characters';
  }
  
  // Customer code should be alphanumeric with hyphens/underscores
  const codePattern = /^[a-zA-Z0-9_-]+$/;
  if (!codePattern.test(customerCode.trim())) {
    return 'Customer Code can only contain letters, numbers, hyphens, and underscores';
  }
  
  return null;
};

/**
 * Validates organization details
 * @param {string} organizationDetails - The organization details to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateOrganizationDetails = (organizationDetails) => {
  // Organization details is optional
  if (!organizationDetails || !organizationDetails.trim()) {
    return null;
  }
  
  if (organizationDetails.length > 1000) {
    return 'Organization Details must not exceed 1000 characters';
  }
  
  return null;
};

/**
 * Validates the entire customer form
 * @param {Object} formData - The form data to validate
 * @param {string} formData.customerName - Customer name
 * @param {string} formData.customerCode - Customer code
 * @param {string} formData.organizationDetails - Organization details
 * @param {boolean} formData.isActive - Active status
 * @param {boolean} formData.augmentationEnabled - Augmentation enabled status
 * @returns {Object} Validation result with isValid flag and errors object
 */
export const validateCustomerForm = (formData) => {
  const errors = {};
  
  // Validate customer name (required)
  const nameError = validateCustomerName(formData.customerName);
  if (nameError) {
    errors.customerName = nameError;
  }
  
  // Validate customer code (optional but must be valid if provided)
  const codeError = validateCustomerCode(formData.customerCode);
  if (codeError) {
    errors.customerCode = codeError;
  }
  
  // Validate organization details (optional but must be valid if provided)
  const orgError = validateOrganizationDetails(formData.organizationDetails);
  if (orgError) {
    errors.organizationDetails = orgError;
  }
  
  // Boolean fields don't need validation as they're controlled by switches
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Gets a user-friendly error message for a specific field
 * @param {Object} errors - Errors object from validation
 * @param {string} fieldName - Name of the field
 * @returns {string|null} Error message or null
 */
export const getFieldError = (errors, fieldName) => {
  return errors[fieldName] || null;
};
